import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, Clock, BookOpen, ArrowRight } from "lucide-react";
import SharedNavbar from "../components/SharedNavbar";

const APP_URL = "";

const ARTICLES = [
  {
    slug: "how-to-track-rvus",
    title: "How to Track RVUs (Complete Guide for Providers)",
    excerpt:
      "Tracking RVUs is essential for productivity-based compensation. Learn how RVUs work, why they matter, and how accurate tracking can directly impact your income and career growth.",
    category: "RVU Fundamentals",
    readTime: "6 min read",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwcm92aWRlciUyMG1lZGljYWwlMjB0YWJsZXQlMjBkYXRhJTIwdHJhY2tpbmclMjBjbGluaWNhbHxlbnwwfHx8fDE3NzMzNjQ5Mzh8MA&ixlib=rb-4.1.0&q=85",
    categoryColor: "blue",
  },
  {
    slug: "missed-billing-codes",
    title: "Common Missed Billing Codes (Primary/Urgent Care/ER)",
    excerpt:
      "Missed billing codes in urgent care, primary care, and the ER can cost providers thousands annually. Learn the most common missed codes and how tracking your RVUs helps prevent lost income.",
    category: "Billing & Coding",
    readTime: "10 min read",
    image:
      "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwYmlsbGluZyUyMGNvZGluZyUyMGhvc3BpdGFsJTIwY2hhcnQlMjBwYXBlcndvcmt8ZW58MHx8fHwxNzczNDM5ODM1fDA&ixlib=rb-4.1.0&q=85",
    categoryColor: "orange",
  },
  {
    slug: "rvus-and-paycheck",
    title: "How RVUs Translate Into Your Paycheck",
    excerpt:
      "Many clinicians know RVUs measure productivity — but few understand how they directly translate into income. Learn how compensation models, conversion rates, and RVU tracking impact your paycheck.",
    category: "RVU & Compensation",
    readTime: "8 min read",
    image:
      "https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwaHlzaWNpYW4lMjBjb250cmFjdCUyMG5lZ290aWF0aW9uJTIwaW5jb21lJTIwZmluYW5jaWFsJTIwcGxhbm5pbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczNTE0OTQ3fDA&ixlib=rb-4.1.0&q=85",
    categoryColor: "green",
  },
];

function useFadeIn(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}


const categoryColors = {
  blue:   { bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200"   },
  green:  { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200"  },
  purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
};

function ArticleCard({ article, delay = 0 }) {
  const c = categoryColors[article.categoryColor] || categoryColors.blue;
  return (
    <FadeIn delay={delay}>
      <a
        href={`/rvu-articles/${article.slug}`}
        data-testid={`article-card-${article.slug}`}
        className="group block bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${c.bg} ${c.text} ${c.border}`}>
              {article.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Clock size={11} />
              {article.readTime}
            </span>
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2 leading-snug group-hover:text-blue-700 transition-colors">
            {article.title}
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-3">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
              Read Article <ArrowRight size={14} />
            </span>
          </div>
        </div>
      </a>
    </FadeIn>
  );
}

export default function RVUArticles() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    document.title = "RVU Articles & Resources for Providers | Doc RVU Tracker";
    if (metaDesc) metaDesc.setAttribute("content", "Expert articles on RVU tracking, wRVU productivity, physician compensation, and maximizing earnings for healthcare providers.");
    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", background: "#f8fafc" }}>
      <SharedNavbar activePage="articles" />

      {/* Hero */}
      <section className="pt-32 pb-14 px-4" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 60%, #f0fdf4 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            <BookOpen size={12} />
            RVU Knowledge Base
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4 leading-tight">
            RVU Articles &{" "}
            <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Resources
            </span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Guides and resources on RVU tracking, wRVU productivity, and healthcare compensation — for providers who know their worth.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {ARTICLES.map((article, i) => (
              <ArticleCard key={article.slug} article={article} delay={i * 80} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <div
              className="rounded-3xl p-10 text-center"
              style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", boxShadow: "0 20px 60px rgba(29,78,216,0.20)" }}
            >
              <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 tracking-tight">
                Ready to Track Your RVUs with ONE CLICK?
              </h2>
              <p className="text-slate-300 text-base mb-7 max-w-lg mx-auto">
                Use Doc RVU Tracker, the Fastest RVU Tracker in the market. <span className="text-blue-400 font-semibold">PERIOD.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`${APP_URL}/register`}
                  data-testid="articles-cta-register"
                  className="inline-flex items-center justify-center gap-2 text-base font-bold text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-px hover:shadow-xl"
                  style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 20px rgba(37,99,235,0.40)" }}
                >
                  Try Doc RVU Tracker Free <ChevronRight size={18} />
                </a>
                <a
                  href="/free-rvu-calculator"
                  className="inline-flex items-center justify-center gap-2 text-base font-semibold text-slate-300 border border-slate-600 rounded-full px-8 py-3.5 hover:bg-white/5 transition-all"
                >
                  Free wRVU Calculator
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 30, height: 30, borderRadius: "7px", objectFit: "cover" }} />
              <span className="text-white font-semibold text-sm">Doc RVU Tracker</span>
            </div>
            <p className="text-xs text-slate-500">The One-Click tracker built for providers by providers.</p>
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} Doc RVU Tracker. All rights reserved.</p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1">
            <div className="flex gap-5 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <a href="/rvu-articles" className="hover:text-white transition-colors">Articles</a>
              <a href="/free-rvu-calculator" className="hover:text-white transition-colors">Free Calculator</a>
              <a href={`${APP_URL}/login`} className="hover:text-white transition-colors">Log In</a>
              <a href={`${APP_URL}/register`} className="hover:text-white transition-colors">Sign Up</a>
            </div>
            <p className="text-sm font-medium text-slate-300 mt-1">DocRVUTracker@gmail.com</p>
          </div>
        </div>
      </footer>

      <style>{`
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}
